import { Check, Circle } from 'lucide-react';

const initials = (name) => (String(name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?');

/**
 * Kopf der Personal DNA: „So sieht dich die KI“ auf einen Blick, mit Vollständigkeit.
 * Nur Anzeige. Ein Klick auf einen Punkt springt in den passenden Tab.
 */
export const PersonalDnaOverview = ({ s, hasVisuals, onGo, lang = 'de' }) => {
  const T = (de, en) => (lang === 'de' ? de : en);
  const p = s.profile;
  if (!p) return null;
  const roles = (p.roles || []).filter(r => r.job || r.company);
  const position = roles.length >= 2
    ? roles.map(r => [r.job, r.company].filter(Boolean).join(' @ ')).join('  ·  ')
    : [p.job, p.company].filter(Boolean).join(' @ ');
  const topics = (p.topics || []).filter(t => t.title).slice(0, 5);
  const voiceLine = s.voice?.summary || '';

  const checks = [
    { id: 'du', label: T('Profil', 'Profile'), done: !!(p.positioning && p.audience && topics.length), hint: T('Positionierung, Zielgruppe, Themen', 'Positioning, audience, topics') },
    { id: 'werdegang', label: T('Werdegang', 'Career'), done: !!((p.career || []).length || s.cvSources.length), hint: T('Lebenslauf hochladen', 'Upload your CV') },
    { id: 'stimme', label: T('Stimme', 'Voice'), done: !!voiceLine, hint: s.samples ? T(`${s.samples} eigene Posts, jetzt analysieren`, `${s.samples} own posts, analyse now`) : T('Eigene Posts hinzufügen', 'Add your own posts') },
    { id: 'aussehen', label: T('Aussehen', 'Look'), done: !!hasVisuals, hint: T('Farben und Logo', 'Colours and logo') },
  ];
  const doneCount = checks.filter(c => c.done).length;

  return (
    <section className="rounded-xl border border-[#1A2B42] bg-[#0F1D32] p-4 sm:p-5 mb-5" data-testid="dna-overview">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-[#00D9C8]/15 text-[#00D9C8] flex items-center justify-center text-base font-semibold shrink-0" aria-hidden>{initials(p.name)}</div>
        <div className="min-w-0 flex-1">
          <h2 className="text-white text-lg font-semibold truncate">{p.name || T('Dein Name', 'Your name')}</h2>
          {position && <p className="text-sm text-[#A0AEC0]">{position}</p>}
        </div>
      </div>

      <div className="mt-5 space-y-4">
        {p.positioning
          ? <p className="text-[15px] leading-snug text-[#E6EDF5] border-l-2 border-[#00D9C8] pl-3">{p.positioning}</p>
          : <p className="text-sm text-[#5A6C7D] border-l-2 border-[#2A3B52] pl-3">{T('Noch keine Positionierung. Sie entsteht aus deinem Profil.', 'No positioning yet. It is drawn from your profile.')}</p>}
        {topics.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="dna-overview-topics">
            {topics.map(t => <span key={t.title} className="rounded-full bg-[#1A2B42] px-2.5 py-1 text-xs text-white">{t.title}</span>)}
          </div>
        )}
        {(p.audience || voiceLine) && (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
            {p.audience && <div><dt className="uppercase tracking-wide text-[#6B7C90] mb-0.5">{T('Für wen', 'For whom')}</dt><dd className="text-[#C5D0DD] line-clamp-2">{p.audience}</dd></div>}
            {voiceLine && <div><dt className="uppercase tracking-wide text-[#6B7C90] mb-0.5">{T('So klingst du', 'How you sound')}</dt><dd className="text-[#C5D0DD] line-clamp-2">{voiceLine}</dd></div>}
          </dl>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-[#1A2B42]" data-testid="dna-checklist">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] uppercase tracking-wide text-[#6B7C90]">{T('Vollständigkeit', 'Completeness')}</p>
          <p className="text-xs text-[#8696A9]">{doneCount}/{checks.length}</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {checks.map(c => (
            <button type="button" key={c.id} onClick={() => onGo && onGo(c.id)} className={`flex items-start gap-2 rounded-lg px-2.5 py-2 text-left transition-colors ${c.done ? 'bg-[#00D9C8]/10 hover:bg-[#00D9C8]/15' : 'bg-[#1A2B42] hover:bg-[#223650]'}`} data-testid={`dna-check-${c.id}`}>
              {c.done ? <Check className="w-4 h-4 text-[#00D9C8] mt-0.5 shrink-0" /> : <Circle className="w-4 h-4 text-[#5A6C7D] mt-0.5 shrink-0" />}
              <span className="min-w-0"><span className="block text-sm text-white">{c.label}</span>{!c.done && <span className="block text-[11px] text-[#8696A9] leading-tight">{c.hint}</span>}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PersonalDnaOverview;
